import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { cancelList, populateLazyListingsWithAssets, updateList } from 'utils/auction.util'
import { sendTransactionWithRetry } from 'utils/transactions'

// next components
import Image from 'next/image'

// third party components

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend)

// components
import ProgressingModal from 'components/Modal/ProgressingModal'
import Alert from 'components/Alert'
import MakeAnOfferModal from 'components/Modal/MakeAnOfferModal'
import ReportAnIssueModal from 'components/Modal/ReportAnIssueModal'
// custom functions
import { useViewport } from 'utils'
import { useAuthentication } from 'hooks/useAuthentication'
import { getNFTCategories, getNFTTokenAccount, getNFTArtistPublicKey, getNftFromDAS } from 'utils/nfts.utils'
import { loadAuctionHouse, findListingReceipt } from 'utils/auction.util'

import {
  cancelListing,
  fetchAuctionListing,
  getBuyerEscrowBalanceDeficit,
  getListingConfig,
  parseAuctionListing,
  placeBid,
} from 'utils/auctioneer.util'

// images and icons
import HeartFilledWhiteIcon from 'assets/icons/heart-filled-white-46x46.svg'
import HeartFilledRedIcon from 'assets/icons/heart-filled-red-46x46.svg'
import TicketWhiteIcon from 'assets/icons/ticket-white-46x46.svg'
import TicketGreenIcon from 'assets/icons/ticket-green-46x46.svg'
import DefaultNftBG from 'assets/images/default-nft-bg.png'
import ThreeDotIcon from 'assets/icons/three-dot-icon.svg?component'

// json
import FullScreenModeIcon from 'assets/icons/fullscreen-mode.svg?component'
import ShopApparelList from 'assets/json/browse-nft/shop-apparel-list.json'

// styles
import styles from './index.module.scss'

// solana
import { buyNFT, offerNFT } from 'utils/auction.util'
import { useMetaplex } from 'hooks/useMetaplex'
import { LAMPORTS_PER_SOL, PublicKey, Transaction } from '@solana/web3.js'
import { toLazyListing, toListingReceiptAccount, GpaBuilder } from '@metaplex-foundation/js'
import type { Sft, Metadata, Nft } from '@metaplex-foundation/js'
import type { ListingConfig as AuctionListing } from '@metaplex-foundation/mpl-auctioneer'
import { useSolToUsdRate } from 'hooks/useSolToUsdRate'
import { cancelListingEdition, createPrint } from 'apis/editions'

import { useWallets } from 'hooks/useWallets'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { CDN_PREFIX, getTimeDifferenceWithAuction } from 'utils/helpers'
import ModalPlaceBid from 'components/Modal/ModalPlaceBid'
import AuctionCard from 'components/Card/AuctionCard'
import SocialShareModal from 'components/Menu/SocialShareModal'
import ReportMobileModal from 'components/Menu/ReportMobileModal'

import 'react-placeholder/lib/reactPlaceholder.css'
import LikeModal from 'components/Modal/LikeModal'
import { getShortAddress } from 'utils/wallet.util'
import { executeSale } from 'utils/auctioneer.util'
import EscrowDeficientModal from 'components/Modal/EscrowDeficientModal'
import CreateMerchSudioModal from 'components/Modal/CreateMerchSudioModal'
import RemoveMerchSudioModal from 'components/Modal/RemoveMerchSudioModal'
import { getExplorerUrl } from 'utils/explorer'
import UpdateNFTModal from 'components/Modal/UpdateNFTModal'
import { NftData } from 'types/nft'
import AuctionDetail from '../AuctionDetail'
import NFTCreatorDataSection from './NFTCreatorDataSection'
import NFTAssetSection from './NFTAssetSection'
import EditMetaModal from 'components/Modal/EditMetaModal'
import ListEditionDetail from '../ListEditionDetail'

// call apis
import nftsAPI from 'apis/nfts'
import usersAPI from 'apis/user'
import listingsAPI from 'apis/listings'
import AuctionAPI from 'apis/auctions'
import { getListedMasterEditionInfo } from 'apis/editions'
import { getPriceValue } from 'utils/strings'
import MintEditionCard from 'components/Card/MintEditionCard'
import { revokeDelegateEdition } from 'utils/editions'
import MenuEditNft from 'components/Menu/MenuEditNft'
import NFTDetailSection from './NFTDetailSection'
import OutlineButton from 'components/Button/OutlineButton'
import GradientButton from 'components/Button/GradientButton'
import NFTHistorySection from './NFTHistorySection'
import NFTCategorySection from './NFTCategorySection'
import ShopApparelSection from '../ShopApparelSection'
import NFTDescriptionSection from './NFTDescriptionSection'
import NFTEditionMerchCard from './NFTEditionMerchCard'
import NFTMakeOfferSection from './NFTMakeOfferSection'
import NFTPlaceBidSection from './NFTPlaceBidSection'
import LoadingButton from 'components/Button/LoadingButton'
import NFTMerchSection from './NFTMerchSection'
import NFTMoreSection from './NFTMoreSection'
import ListArtworkDetail from '../ListArtworkDetail'
import NftMenuEditModal from 'components/Modal/NftMenuEditModal'
import { createUpdatePrimarySaleHappenedViaTokenInstruction } from '@metaplex-foundation/mpl-token-metadata'

export interface MainSectionProps {
  nftDetail: NftData
  editionDetail: any
  nftInfo: any
  setFullScreenView: any
  reloadNftData: any
  setOpenMerchPage: any
  setMerchData: any
  fullScreen: boolean
  setFullScreenMerch: any
  setMerchImages: any
}

const MainSection = ({
  nftDetail,
  editionDetail,
  nftInfo,
  setFullScreenView,
  reloadNftData,
  setOpenMerchPage,
  setMerchData,
  fullScreen,
  setFullScreenMerch,
  setMerchImages,
}: MainSectionProps) => {
  const viewport = useViewport()
  const router = useRouter()
  const { id } = router.query
  const dispatch = useDispatch()
  const { refreshToken } = useAuthentication()
  const { connected, publicKey } = useWallet()
  const { setVisible } = useWalletModal()
  const { solToUsdRate } = useSolToUsdRate()
  const [nftLoading, setNftLoading] = useState<boolean>(true)
  const { connection } = useConnection()
  const explorerURL = useMemo(() => getExplorerUrl(connection.rpcEndpoint, id as string), [id, connection])
  const [openShareModal, setOpenShareModal] = useState(false)
  const [nftReservePrice, setNftReservePrice] = useState(0)
  const [nftCurrentPrice, setNftCurrentPrice] = useState(0)
  const [reservedPrice, setReservedPrice] = useState(0)
  const [highestBidAmount, setHighestBidAmount] = useState(0)
  const [minBidIncrement, setMinBidIncrement] = useState<number>(0)
  const [sellerAddress, setSellerAddress] = useState<PublicKey>()
  const [tokenAccountInfo, setTokenAccountInfo] = useState(null)
  const [highBidder, setHighBidder] = useState('')
  const [endTime, setEndTime] = useState<string>(null)
  const [startTime, setStartTime] = useState<string>(null)
  const [endPhaseDuration, setEndPhaseDuration] = useState<number>(0)
  const [isEnded, setIsEnded] = useState(false)
  const [isStarted, setIsStarted] = useState(false)
  const [showPlaceBidButton, setShowPlaceBIdButton] = useState(false)
  const [liveAuction, setLiveAuction] = useState(false)
  const [showAuctionDetail, setShowAuctionDetail] = useState(false)
  const [showListEditionDetail, setShowListEditionDetail] = useState(false)
  const [showListArtworkDetail, setShowListArtworkDetail] = useState(false)
  const [listEditionType, setListEditionType] = useState('list')
  const [showUpdateListNFTModal, setShowUpdateListNFTModal] = useState(false)
  const [showNftMenuEditModal, setShowNftMenuEditModal] = useState(false)
  const [currentSupply, setCurrentSupply] = useState(editionDetail ? editionDetail.supply : 0)
  const [auctionLoading, setAuctionLoading] = useState<boolean>(false)
  const [buyLoading, setBuyLoading] = useState<boolean>(false)
  const [openReportModal, setOpenReportModal] = useState(false)

  const [openReportMenuModal, setOpenReportMenuModal] = useState(false)
  const [showModalPlaceBid, setShowModalPlaceBid] = useState(false)
  const [like, setLike] = useState<boolean>(false)
  const [likeCount, setLikeCount] = useState(0)
  const [showLikeModal, setShowLikeModal] = useState(false)
  const [share, setShare] = useState<boolean>(false)
  const [shareCount, setShareCount] = useState(0)
  const [save, setSave] = useState<boolean>(false)
  const [saveCount, setSaveCount] = useState(0)
  const [showNftMeta, setShowNftMeta] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [isOwnNft, setIsOwnNft] = useState(null)
  const [auctionInfoFocus, setAuctionInfoFocus] = useState(false)
  const [price, setPrice] = useState<string>('')
  const [mintQty, setMintQty] = useState(1)
  const [mintEditionLoading, setMintEditionLoading] = useState<boolean>(false)
  const [merchCard, setMerchCard] = useState(null)
  const [openProgressingModal, setOpenProgressingModal] = useState(false)
  const [isFinalizeProcessing, setIsFinalizeProcessing] = useState(false)
  const [isCancelProcessing, setIsCancelProcessing] = useState(false)
  const [showAlert, setShowAlert] = useState(false)

  const [openOfferModal, setOpenOfferModal] = useState(false)
  const [deficencyModal, setDeficencyModal] = useState(false)
  const [deficiency, setDeficiency] = useState(0)
  const [finalizeData, setFinalizeData] = useState(null)
  const [alertData, setAlertData] = useState<any[]>([])
  const [descCount, setDescCount] = useState(280)

  useEffect(() => {
    ;(async () => {
      if (!nftDetail) return
      let tokenAccount = await getNFTTokenAccount(mx, new PublicKey(nftDetail.mintAddress))
      setSellerAddress(tokenAccount.owner)

      const creators = nftDetail.creators.map(c => ({
        ...c,
        address: new PublicKey(c.address),
      }))

      let createAddress = getNFTArtistPublicKey({ creators })
      let { username: creatorName, profile_picture, id: user_id } = await usersAPI.get(createAddress)
      setNftCreatorId(user_id)
      setNftCreatorname(creatorName !== null && creatorName.length > 0 ? creatorName : getShortAddress(createAddress))
      setNftCreatorUrl(profile_picture)
      if (createAddress.toBase58() != tokenAccount.owner.toBase58()) {
        let { username: ownerUsername } = await usersAPI.get(tokenAccount.owner.toBase58())
        setNftOwnerUsername(ownerUsername ?? getShortAddress(tokenAccount.owner))
      } else {
        setNftOwnerUsername(creatorName)
      }

      setNameLoading(false)
      setTokenAccountInfo(tokenAccount)

      if (nftDetail.collection) {
        const collectionMetadata = await getNftFromDAS(mx, nftDetail.collection.address)
        if (collectionMetadata) {
          setNftCollectionImage(collectionMetadata.content.links.image)
          setNftCollectionName(collectionMetadata.content.metadata.name)
        }
      }
    })()

    setAlertData([
      {
        id: 0,
        title: 'Your purchase was successful',
        description: 'Navigate to My Dashboard to view your purchased NFT.',
        type: 'success',
      },
    ])

    function handleResize() {
      let screenWidth = window.innerWidth
      let count =
        screenWidth >= 1900
          ? 440
          : screenWidth >= 1768
          ? 400
          : screenWidth >= 1400
          ? 300
          : screenWidth >= 1280
          ? 250
          : screenWidth >= 1024
          ? 200
          : 300
      setDescCount(count)
    }

    window.addEventListener('resize', handleResize)

    handleResize()

    return () => window.removeEventListener('resize', handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isStarted && !isEnded) {
      setShowPlaceBIdButton(true)
    } else {
      setShowPlaceBIdButton(false)
    }
  }, [isEnded, isStarted])

  const nftType: string = (nftDetail.category as string) ?? 'image'
  const coverUrl = nftDetail?.image ?? DefaultNftBG.src
  const nftCoverUrl = coverUrl.includes('=gif') ? coverUrl.replace(CDN_PREFIX, '') : coverUrl
  const originalCoverUrl = nftDetail.original.image
  const nftAssetUrl: string = nftDetail?.animation_url as string
  const nftName = nftDetail?.name ?? 'No name found'
  const nftDescription = nftDetail?.description ?? ''
  const nftRoyalties = (nftDetail.sellerFeeBasisPoints / 100).toString()
  const nftCategories = getNFTCategories(nftDetail)
  const mintAddress = nftDetail.mintAddress.toString()
  const nftCollectionAddress = nftDetail.collection?.address?.toString()
  const supply = nftDetail.supply
  const [nftCollectionName, setNftCollectionName] = useState('')
  const [nftCollectionImage, setNftCollectionImage] = useState('')

  const [nftPrice, setNftPrice] = useState(0)
  const [nftOwnerUsername, setNftOwnerUsername] = useState('')
  const [nftCreatorname, setNftCreatorname] = useState('')
  const [nftCreatorUrl, setNftCreatorUrl] = useState(null)
  const [nftCreatorId, setNftCreatorId] = useState(null)
  const [nftListingData, setNftListingData] = useState(null)
  const [tradeStateAddress, setTradeStateAddress] = useState('')
  const [merchToggle, setMerchToggle] = useState(false)
  const [createMerchModal, setCreateMerchModal] = useState(false)
  const [removeMerchModal, setRemoveMerchModal] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [editionLoading, setEditionLoading] = useState(false)
  const [nameLoading, setNameLoading] = useState(true)
  const [editionType, setEditionType] =
    useState<'normal' | 'master_le' | 'master_oe' | 'print_le' | 'print_oe'>('normal')
  const [isEditionListing, setIsEditionListing] = useState(false)
  const [cancelListLoading, setCancelListLoading] = useState(false)

  const minBidAmount = highestBidAmount > 0 ? highestBidAmount + minBidIncrement : reservedPrice

  const { metaplex: mx } = useMetaplex()
  const { mainBalance: mainWalletBalance, biddingBalance, offerBalance, updateSolBalance } = useWallets()
  const bidWalletLock = useSelector(state => (state as any).bidWalletLock)

  function getMintAddress(asset: Nft | Sft | Metadata): PublicKey {
    if ('mintAddress' in asset) return asset.mintAddress
    else return asset.mint.address
  }

  const quickBuy = async () => {
    if (!connected) {
      setVisible(true)
      return
    }

    if (nftPrice > mainWalletBalance - bidWalletLock) {
      setAlertData([
        {
          id: 0,
          title: 'Insufficient Funds',
          description: 'Not enough funds in wallet for purchase',
          type: 'error',
        },
      ])
      setShowAlert(true)
      return
    }

    try {
      setBuyLoading(true)
      await refreshToken()

      const result = await buyNFT(mx, mintAddress, tradeStateAddress, nftPrice)
      if (result) {
        await updateSolBalance()
        listingsAPI.quickBuy(mintAddress, { buyer_wallet_id: publicKey?.toString(), amount: nftPrice.toString() }).then(
          response => {
            setNftLoading(true)
            setNftPrice(0)
            setShowAlert(true)
            reloadNftData()
            setIsOwnNft(true)
          },
          error => console.log(error)
        )
        // router.reload()
      } else {
        setBuyLoading(false)
      }
    } catch {
      setBuyLoading(false)
    }
  }

  const mintEdition = async () => {
    if (!connected) {
      setVisible(true)
      return
    }

    setMintEditionLoading(true)

    let editionsToPrint = undefined
    let editionStatuses: Record<string, any> = {}
    for (let i = 0; i < 5 && Object.keys(editionStatuses).length < mintQty; i++)
      try {
        let printResponse = await createPrint({
          mintAddress,
          buyer: mx.identity().publicKey.toBase58(),
          count: mintQty,
          editions: editionsToPrint,
        })

        if (printResponse.length == 0) break

        editionsToPrint = printResponse.map(({ editionNumber }) => editionNumber)
        let txs = printResponse.map(({ transaction }) => Transaction.from(Buffer.from(transaction, 'base64')))
        let signedTransactions = await mx.identity().signAllTransactions(txs)

        let results = await Promise.allSettled(
          signedTransactions.map(async (tx, index) => {
            let { editionNumber, editionMint, lastValidBlockHeight } = printResponse[index]
            let signature = await sendTransactionWithRetry({
              connection: mx.connection,
              transaction: tx,
              maxHeight: lastValidBlockHeight,
            })
            if (!signature) {
              console.error('could not confirm signature for', printResponse[index])
              throw new Error(`could not confirm signature for ${editionNumber} ${editionMint}`)
            }

            await nftsAPI
              .create({
                token_id: editionMint,
                nft_name: `${nftName} #${editionNumber}`,
                categories: nftCategories.toString(),
                asset_type: nftType === 'image/gif' ? 'image' : nftType,
                cover_image_url: nftCoverUrl,
                asset_url: nftAssetUrl ?? originalCoverUrl,
                collection_id: nftCollectionAddress ?? '',
                edition: editionNumber,
                parent_nft: mintAddress,
                max_supply: editionType == 'master_le' ? supply.max : null,
              })
              .then(
                response => {},
                error => {
                  console.log(error)
                }
              )

            editionStatuses[editionNumber.toString()] = {
              status: 'success',
              signature,
              editionMint,
            }
          })
        )

        break
      } catch (e: any) {
        console.error(`attempt ${i + 1} failed: ${e?.message ?? e}`)
        console.error(e)
        setMintEditionLoading(false)

        setAlertData([
          {
            id: 0,
            title: 'Purchase canceled',
            description: `Wallet signature request rejected`,
            type: 'success',
          },
        ])
        setShowAlert(true)

        if (e?.message == 'User rejected the request.') break
        await new Promise(resolve => setTimeout(resolve, 1000))
        return
      }

    setMintEditionLoading(false)

    if (Object.keys(editionStatuses).length === 0) {
      setAlertData([
        {
          id: 0,
          title: 'Purchase canceled',
          description: `Wallet signature request rejected`,
          type: 'success',
        },
      ])
      setShowAlert(true)
    } else if (Object.keys(editionStatuses).length === mintQty) {
      console.log('minted editions successfully', editionStatuses)

      setAlertData([
        {
          id: 0,
          title: 'Your edition successfully purchased!',
          description: `You've successfully purchased ${nftName} #${editionsToPrint[0]}`,
          type: 'success',
        },
      ])
      setShowAlert(true)
      setCurrentSupply(currentSupply + 1)
    } else {
      setAlertData([
        {
          id: 0,
          title: 'Mint partially succeeded!',
          description: `You've failed minting some editions`,
          type: 'error',
        },
      ])
      setShowAlert(true)
    }
  }

  const makeOffer = async price => {
    setOpenProgressingModal(true)

    await refreshToken()

    const result = await offerNFT(mx, mintAddress, parseFloat(price))
    await updateSolBalance()
    if (result) {
      listingsAPI.makeOffer(mintAddress, { amount: price.toString() }).then(response => {
        setAlertData([
          {
            id: 0,
            title: 'Your order has been placed successfully!',
            description: `You've successfully placed an offer on ${nftName}`,
            type: 'success',
          },
        ])
        setShowAlert(true)

        usersAPI.getLockupValues().then(
          response => {
            dispatch({ type: 'set', bidWalletLock: response.bid_wallet_lock })
            dispatch({ type: 'set', offerWalletLock: response.offer_wallet_lock })
          },
          error => {
            console.log(error)
          }
        )
      }),
        error => {
          console.log(error)
        }
    }
    setOpenOfferModal(false)
    setOpenProgressingModal(false)
  }

  useEffect(() => {
    if (nftInfo) {
      let { merch_hub_enabled, likes, shares, saves, liked, saved, shared, auction } = nftInfo
      let sharedSocial = shared?.facebook || shared?.twitter || shared?.telegram
      setHighBidder(nftInfo?.auction?.highest_bid?.buyer_address ?? '')
      setMerchToggle(merch_hub_enabled)
      setLikeCount(likes)
      setShareCount(shares)
      setSaveCount(saves)
      setLike(liked)
      setSave(saved)
      setShare(sharedSocial)
      setEndPhaseDuration(auction?.auction?.end_phase_duration ?? 0)
      if (nftInfo.master_edition?.merchProduct) {
        setMerchCard(nftInfo.master_edition?.merchProduct)
        setMerchImages(nftInfo.master_edition?.merchProduct.options.addons)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nftInfo])

  // set timeout to switch to end phase
  useEffect(() => {
    if (!nftInfo) return

    let setEndPhaseTimeoutHandle: number | null = null

    let endPhaseMinBidIncrement = nftInfo.auction?.auction?.end_phase_bid_increment ?? 0
    function endPhaseStarted() {
      setMinBidIncrement(endPhaseMinBidIncrement)
      setPrice((highestBidAmount + endPhaseMinBidIncrement).toFixed(2))
    }

    if (endPhaseDuration > 0 && endPhaseMinBidIncrement > 0 && liveAuction && !isEnded) {
      let endTimestampMs = new Date(endTime).getTime()
      let endPhaseStartsIn = endTimestampMs - Date.now() - endPhaseDuration * 1000
      if (endPhaseStartsIn <= 0) endPhaseStarted()
      else setTimeout(endPhaseStarted, endPhaseStartsIn)
    }

    if (setEndPhaseTimeoutHandle != null) return () => clearTimeout(setEndPhaseTimeoutHandle)
  }, [endPhaseDuration, liveAuction, isEnded, endTime, highestBidAmount, nftInfo])

  useEffect(() => {
    if (connected && publicKey !== null && sellerAddress !== null) {
      setIsOwnNft(publicKey?.toBase58() === sellerAddress?.toBase58())
    } else {
      setIsOwnNft(false)
    }
  }, [connected, publicKey, sellerAddress])

  const handleAuctionData = useCallback(
    async (auctionListing: AuctionListing) => {
      if (!auctionListing) return
      setLiveAuction(true)

      // use to set auction info
      let {
        startTime,
        endTime,
        minBidIncrement: minBidIncrementBN,
        reservePrice,
        highestBid: { amount },
      } = auctionListing
      let minBidInc = parseFloat(minBidIncrementBN.toString()) / LAMPORTS_PER_SOL
      let reservedPrice = parseFloat(reservePrice.toString()) / LAMPORTS_PER_SOL
      let highestBidAmount = parseFloat(amount.toString()) / LAMPORTS_PER_SOL
      let minBidAmount = highestBidAmount > 0 ? highestBidAmount + minBidInc : reservedPrice

      setReservedPrice(reservedPrice)
      setNftReservePrice(reservedPrice)

      setHighestBidAmount(highestBidAmount)
      setNftCurrentPrice(highestBidAmount)

      setStartTime(new Date(parseInt(startTime.toString()) * 1000).toISOString())
      let endTimestamp = parseInt(endTime.toString())
      setEndTime(new Date(endTimestamp * 1000).toISOString())
      if (!minBidIncrement) {
        setMinBidIncrement(minBidInc)
        setPrice(minBidAmount.toFixed(2))
      }

      if (endTimestamp < Math.floor(Date.now() / 1000)) setIsEnded(true)
      setNftLoading(false)
    },
    [minBidIncrement]
  )

  const getEditionListingStatus = () => {
    setEditionLoading(true)
    getListedMasterEditionInfo(nftDetail.mintAddress).then(
      response => {
        setEditionLoading(false)
        if (response && response.price.length > 0) {
          setIsEditionListing(true)
          setPrice(getPriceValue(Number(response.price)))
          setNftPrice(parseFloat(getPriceValue(Number(response.price))))
        }
      },
      error => {
        setEditionLoading(false)
      }
    )
  }

  useEffect(() => {
    if (!supply) {
      return
    }
    if (supply.max === 0) {
      setEditionType('normal')
    } else if (supply.max === null) {
      if (supply.parent === null) {
        setEditionType('master_oe')
        getEditionListingStatus()
      } else {
        setEditionType('print_oe')
        fetch(`/nft/${supply.parent}`)
      }
    } else if (supply.max > 0) {
      if (supply.parent === null) {
        setEditionType('master_le')
        getEditionListingStatus()
      } else {
        setEditionType('print_le')
        fetch(`/nft/${supply.parent}`)
      }
    } else {
      setEditionType('normal')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supply])

  const isNftPrintEdition = () => {
    if (editionType === 'print_oe' || editionType === 'print_le') return true
    return false
  }

  const isNftMasterEdition = () => {
    if (editionType === 'master_oe' || editionType === 'master_le') return true
    return false
  }

  useEffect(() => {
    if (!nftInfo || !tokenAccountInfo) return

    let auctionListenerHandle: number | null = null
    ;(async () => {
      setNftLoading(true)

      if (tokenAccountInfo.delegate && tokenAccountInfo.delegate?.equals(mx.auctionHouse().pdas().programAsSigner())) {
        if (nftInfo.listing) {
          try {
            let listPrice = Math.round(parseFloat(nftInfo.listing.list_price) * LAMPORTS_PER_SOL)
            setNftPrice(listPrice / LAMPORTS_PER_SOL)
            if (editionType !== 'normal') setPrice((listPrice / LAMPORTS_PER_SOL).toFixed(2))

            let listingReceipt = await mx
              .rpc()
              .getAccount(
                findListingReceipt({
                  metaplex: mx,
                  mint: tokenAccountInfo.mint,
                  seller: tokenAccountInfo.owner,
                  price: listPrice,
                })
              )
              .then(toListingReceiptAccount)

            let auctionHouse = await loadAuctionHouse(mx, listingReceipt.data.auctionHouse)

            let listing = toLazyListing(listingReceipt, auctionHouse)
            let {
              tradeStateAddress,
              price: {
                basisPoints,
                currency: { decimals },
              },
            } = listing
            setTradeStateAddress(tradeStateAddress.toBase58())
            // setNftLoading(false)
            let listingAssets = await populateLazyListingsWithAssets(mx, [listing])

            let listingAsset = listingAssets[0]
            let { asset, sellerAddress, price } = listingAsset
            let listingData = {
              address: getMintAddress(asset),
              name: asset?.name,
              uri: asset?.uri,
              sellerAddress,
              tradeStateAddress,
              price: Number(price.basisPoints.toString()),
              creatorAddress: getNFTArtistPublicKey(asset)?.toString(),
              category: nftType,
              animation_url: nftAssetUrl,
              image: nftCoverUrl,
              original: {
                image: originalCoverUrl,
              },
            }
            setNftListingData(listingData)
          } catch (error) {
            setTradeStateAddress(nftDetail.mintAddress)
            let listingData = {
              address: nftDetail.mintAddress,
              name: nftDetail.name,
              uri: nftDetail.uri,
              sellerAddress,
              tradeStateAddress: nftDetail.mintAddress,
              price: Number(nftInfo.listing.list_price),
              creatorAddress: nftDetail.creators[0].address,
              category: nftType,
              animation_url: nftAssetUrl,
              image: nftCoverUrl,
              original: {
                image: originalCoverUrl,
              },
            }
            setNftListingData(listingData)
          }
        } else {
          if (!connected) {
            setIsOwnNft(false)
          }

          let auctionListing = await fetchAuctionListing(
            mx,
            tokenAccountInfo.owner,
            tokenAccountInfo.address,
            tokenAccountInfo.mint
          )
          let [listingConfig] = await getListingConfig(
            tokenAccountInfo.owner,
            tokenAccountInfo.address,
            tokenAccountInfo.mint
          )
          await handleAuctionData(auctionListing)
          auctionListenerHandle = await mx.connection.onAccountChange(
            listingConfig,
            accountInfo => handleAuctionData(parseAuctionListing(accountInfo)),
            'single'
          )
        }
      }
      setTimeout(() => {
        setNftLoading(false)
      }, 1000)

      dispatch({ type: 'set', isLoading: false })

      return () => {
        if (auctionListenerHandle) mx.connection.removeAccountChangeListener(auctionListenerHandle)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nftInfo, tokenAccountInfo])

  const processFinalizeSale = async () => {
    setIsFinalizeProcessing(true)

    let data = {
      address: new PublicKey(nftDetail.mintAddress),
      sellerAddress,
      price: nftCurrentPrice * LAMPORTS_PER_SOL,
      highestBid: { buyer_address: highBidder },
    }

    setFinalizeData(data)

    if (isOwnNft === true) {
      let deficit = await getBuyerEscrowBalanceDeficit(mx, new PublicKey(highBidder), data.price)
      if (deficit > 0) {
        // show confirm dialog
        setDeficiency(deficit / LAMPORTS_PER_SOL)
        setDeficencyModal(true)
        setIsFinalizeProcessing(false)
      } else {
        finalizeSale(data)
      }
    } else {
      finalizeSale(data)
    }
  }

  // Short description feature
  const [isTruncated, setIsTruncated] = useState(true)
  const [tempNftDescription, setTempNftDescription] = useState('')

  // const processedNftDescription = isTruncated ? tempNftDescription.slice(0, descCount) : tempNftDescription
  // const shouldShowReadMore = tempNftDescription.length > descCount

  useEffect(() => {
    setTempNftDescription(nftDescription)
  }, [nftDescription])

  function toggleIsTruncated() {
    setIsTruncated(!isTruncated)
  }

  const reloadCreatorOwnerName = async () => {
    let tokenAccount = await getNFTTokenAccount(mx, new PublicKey(nftDetail.mintAddress))

    setSellerAddress(tokenAccount.owner)

    const creators = nftDetail.creators.map(c => ({
      ...c,
      address: new PublicKey(c.address),
    }))

    let createAddress = getNFTArtistPublicKey({ creators })
    let { username: creatorName, profile_picture } = await usersAPI.get(createAddress)
    setNftCreatorname(creatorName !== null && creatorName.length > 0 ? creatorName : getShortAddress(createAddress))
    setNftCreatorUrl(profile_picture)
    if (createAddress.toBase58() != tokenAccount.owner.toBase58()) {
      let { username: ownerUsername } = await usersAPI.get(tokenAccount.owner.toBase58())
      setNftOwnerUsername(ownerUsername ?? getShortAddress(tokenAccount.owner))
    } else {
      setNftOwnerUsername(creatorName)
    }
  }

  const finalizeSale = async ({ address, sellerAddress, price, highestBid }) => {
    if (!isFinalizeProcessing) setIsFinalizeProcessing(true)

    try {
      await refreshToken()
      const { sale } = await executeSale(mx, address, sellerAddress, new PublicKey(highestBid?.buyer_address), price)
      await updateSolBalance()
      AuctionAPI.executeSale(sale.mintAddress.toBase58(), {
        token_address: sale.mintAddress.toBase58(),
        buyer_wallet_id: sale.buyerAddress.toBase58(),
        sale_price: sale.price.toString(),
      }).then(res => {
        setAlertData([
          {
            id: 0,
            title: 'Your auction successfully finalized!',
            description: `You've successfully finalized your auction!`,
            type: 'success',
          },
        ])

        reloadCreatorOwnerName()
        setNftCurrentPrice(0)
        setLiveAuction(false)
        setEndTime(null)
        setIsFinalizeProcessing(false)
        setTimeout(() => {
          setShowAlert(true)
        }, 500)
        reloadNftData()
      })
    } catch (e) {
      console.error(e)
      setIsFinalizeProcessing(false)
    }
  }

  const cancelAuction = async () => {
    setIsCancelProcessing(true)
    try {
      const { auction } = await cancelListing(mx, new PublicKey(mintAddress), sellerAddress)
      AuctionAPI.cancelAuction(auction.mintAddress.toBase58()).then(res => {
        if (res) {
          setAlertData([
            {
              id: 0,
              title: 'Your auction has been canceled',
              description: `You've successfully canceled the auction`,
              type: 'success',
            },
          ])
          setLiveAuction(false)
          setEndTime(null)
          setIsCancelProcessing(false)
          setTimeout(() => {
            setShowAlert(true)
          }, 500)
        }
      })
    } catch (e) {
      setIsCancelProcessing(false)
    }
  }

  const cancelListEdition = async () => {
    setCancelListLoading(true)
    try {
      let mint = new PublicKey(nftInfo.master_edition?.mintAddress)
      let delegate = new PublicKey(nftInfo.master_edition?.authorityPublicKey)
      let signature = await revokeDelegateEdition({
        metaplex: mx,
        mint,
        delegate,
      })
      if (!signature) {
        console.error(`could not revoke delegate for ${mint} to ${delegate}`)
        throw new Error(`could not revoke delegate for ${mint} to ${delegate}`)
      }

      cancelListingEdition(nftInfo.master_edition?.mintAddress).then(res => {
        if (res) {
          setIsEditionListing(false)
          setCancelListLoading(false)

          setAlertData([
            {
              id: 0,
              title: 'Your listing has been removed',
              description: `${nftName} is no longer available for purchase`,
              type: 'success',
            },
          ])
          setShowAlert(true)
          setTimeout(() => {
            reloadNftData()
          }, 1000)
        }
      })
    } catch (e) {
      setCancelListLoading(false)
    }
  }

  const onCancelList = async ({ address, tradeStateAddress }: { address: string; tradeStateAddress: string }) => {
    setOpenProgressingModal(true)
    try {
      await refreshToken()

      const tradeStateAddressKey =
        typeof tradeStateAddress === 'string' ? new PublicKey(tradeStateAddress) : tradeStateAddress

      const result = await cancelList(mx, tradeStateAddressKey)
      if (result) {
        setNftPrice(0)
        listingsAPI.unlistNft(address.toString()).then(response => {
          if (response) {
            setAlertData([
              {
                id: 0,
                title: 'Your listing has been removed',
                description: `You've successfully removed your fixed-price listing!`,
                type: 'success',
              },
            ])
            reloadNftData()
            setTimeout(() => {
              setShowUpdateListNFTModal(false)
              setOpenProgressingModal(false)
              setTimeout(() => {
                setShowAlert(true)
              }, 800)
            }, 1500)
          }
        }),
          error => {
            setOpenProgressingModal(false)
          }
      } else {
        setOpenProgressingModal(false)
      }
    } catch (e) {
      setOpenProgressingModal(false)
    }
  }

  const onUpdatePrice = async ({ address, tradeStateAddress }, price: number) => {
    setOpenProgressingModal(true)
    try {
      await refreshToken()

      const result = await updateList(mx, address, tradeStateAddress, price)
      if (result) {
        setNftPrice(price / 1000000000)
        listingsAPI.updateNft(address.toBase58(), { amount: (price / 1000000000).toString() }).then(
          response => {
            if (response) {
              setAlertData([
                {
                  id: 0,
                  title: 'Your listing has been updated',
                  description: `You've successfully updated your fixed-price listing!`,
                  type: 'success',
                },
              ])
              setOpenProgressingModal(false)
              setTimeout(() => {
                setShowUpdateListNFTModal(false)
                setTimeout(() => {
                  setShowAlert(true)
                }, 500)
              }, 500)
            }
          },
          error => {
            setOpenProgressingModal(false)
          }
        )
      }
    } catch (e) {
      setOpenProgressingModal(false)
    }
  }

  const placeBidAndSave = async (price: string) => {
    if (price[0] === '.') {
      price = '0' + price
    }

    setAuctionLoading(true)

    try {
      await refreshToken()
      const { bid } = await placeBid(mx, new PublicKey(mintAddress), sellerAddress, parseFloat(price))
      if (bid) {
        updateSolBalance()

        setShowModalPlaceBid(false)
        setNftCurrentPrice(Number(bid.amount.toString()))
        setTimeout(() => {
          setAlertData([
            {
              id: 0,
              title: 'Your bid has been placed!',
              description: `You've successfully placed a bid on ${nftName}`,
              type: 'success',
            },
          ])
          setShowAlert(true)
          setAuctionLoading(false)
        }, 500)
        setHighBidder(publicKey?.toBase58())
        usersAPI.getLockupValues().then(
          response => {
            dispatch({ type: 'set', bidWalletLock: response.bid_wallet_lock })
            dispatch({ type: 'set', offerWalletLock: response.offer_wallet_lock })
          },
          error => {
            console.log(error)
          }
        )
      }
    } catch (error) {
      console.error(error)
      setAuctionLoading(false)
    }
  }

  const changeLikeStatus = () => {
    if (isRunning) return

    let newStatus = !like
    setIsRunning(true)

    if (newStatus) {
      nftsAPI.likeNft(id).then(
        response => {
          if (response !== null) {
            setLikeCount(likeCount + 1)
            setLike(newStatus)
          }
          setIsRunning(false)
        },
        error => {
          setIsRunning(false)
        }
      )
    } else {
      nftsAPI.unlikeNft(id).then(
        response => {
          if (response !== null) {
            setLikeCount(likeCount - 1)
            setLike(newStatus)
          }
          setIsRunning(false)
        },
        error => {
          setIsRunning(false)
        }
      )
    }
  }

  const changeSaveStatus = () => {
    if (isRunning) return

    let newStatus = !save
    setIsRunning(true)

    if (newStatus) {
      nftsAPI.saveNft(id).then(
        response => {
          if (response !== null) {
            setSaveCount(saveCount + 1)
            setSave(newStatus)
          }
          setIsRunning(false)
        },
        error => {
          setIsRunning(true)
        }
      )
    } else {
      nftsAPI.unsaveNft(id).then(
        response => {
          if (response !== null) {
            setSaveCount(saveCount - 1)
            setSave(newStatus)
          }
          setIsRunning(false)
        },
        error => {
          setIsRunning(true)
        }
      )
    }
  }

  const handleSuccessListEdition = nftName => {
    getEditionListingStatus()
    reloadNftData()

    if (listEditionType === 'list') {
      setAlertData([
        {
          id: 0,
          title: 'Your edition was successfully listed!',
          description: `You've successfully published a listing for ${nftName}`,
          type: 'success',
        },
      ])
    } else {
      setAlertData([
        {
          id: 0,
          title: 'Your edition was successfully listed!',
          description: `Your listing for ${nftName} has been updated`,
          type: 'success',
        },
      ])
    }

    setShowAlert(true)
  }

  const handleSuccessAuction = nftName => {
    setAlertData([
      {
        id: 0,
        title: 'Your auction has been published',
        description: `You've successfully published an auction for ${nftName}`,
        type: 'success',
      },
    ])

    setTimeout(() => {
      setShowAlert(true)
    }, 1000)
  }

  const enableMerchToggle = toggle => {
    if (toggle === true) {
      nftsAPI.enableMerchHub(id, { value: true }).then(
        response => {
          if (response !== null) {
            setMerchToggle(true)
            setCreateMerchModal(false)
            setAlertData([
              {
                id: 0,
                title: 'Physical Prints Enabled',
                description: 'This artwork is now available as a physical print for purchase.',
                type: 'success',
              },
            ])
            setShowAlert(true)
          }
        },
        err => {}
      )
    } else {
      nftsAPI.enableMerchHub(id, { value: false }).then(
        response => {
          if (response !== null) {
            setMerchToggle(false)
            setRemoveMerchModal(false)
            setAlertData([
              {
                id: 0,
                title: 'Physical Prints Disabled',
                description: 'This artwork is no longer available as a physical print.',
                type: 'success',
              },
            ])
            setShowAlert(true)
          }
        },
        err => {}
      )
    }
  }

  const onListNftArtwork = async () => {
    setNftLoading(true)

    setTimeout(() => {
      setShowListArtworkDetail(false)
    }, 500)

    let tokenAccount = await getNFTTokenAccount(mx, new PublicKey(nftDetail.mintAddress))
    setSellerAddress(tokenAccount.owner)
    setTokenAccountInfo(tokenAccount)
    reloadNftData()
  }

  const onStartAuction = async () => {
    setNftLoading(true)
    window.scrollTo(0, 0)

    setTimeout(() => {
      setShowAuctionDetail(false)
    }, 500)

    let tokenAccount = await getNFTTokenAccount(mx, new PublicKey(nftDetail.mintAddress))
    setSellerAddress(tokenAccount.owner)
    setTokenAccountInfo(tokenAccount)
    reloadNftData()
  }

  const isNftNormalListing = () => {
    return nftPrice > 0 && !liveAuction && !isNftMasterEdition()
  }

  const isNftAuctionEnded = () => {
    return (
      connected &&
      endTime !== null &&
      getTimeDifferenceWithAuction(endTime) < 0 &&
      (isOwnNft === true || publicKey?.toBase58() === highBidder)
    )
  }

  const isNftAuctionLive = () => {
    // return liveAuction === true && !isEnded
    return liveAuction === true
  }

  const sidebarRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [headerHeight] = useState(80)
  const [sidebarHeight, setSidebarHeight] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(0)

  useEffect(() => {
    setViewportHeight(window.innerHeight)

    const updateSidebarHeight = () => {
      if (sidebarRef.current) {
        setSidebarHeight(sidebarRef.current.scrollHeight)
        setViewportHeight(window.innerHeight)
        setIsInitialized(true)
      }
    }

    updateSidebarHeight()
    window.addEventListener('resize', updateSidebarHeight)
    return () => window.removeEventListener('resize', updateSidebarHeight)
  }, [])

  useEffect(() => {
    if (!sidebarRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setSidebarHeight(entry.contentRect.height)
        setViewportHeight(window.innerHeight)
      }
    })

    resizeObserver.observe(sidebarRef.current)

    return () => resizeObserver.disconnect()
  }, [sidebarRef])

  useEffect(() => {
    if (!isInitialized) return

    const handleScroll = () => {
      if (!sidebarRef.current || !contentRef.current) return

      const scrollTop = window.scrollY
      const sidebarScrollHeight = sidebarRef.current.scrollHeight
      const sidebarVisibleHeight = viewportHeight - headerHeight
      const maxSidebarScroll = Math.max(0, sidebarScrollHeight - sidebarVisibleHeight)

      const sidebarTransform = Math.min(scrollTop, maxSidebarScroll)

      requestAnimationFrame(() => {
        if (sidebarRef.current) {
          sidebarRef.current.style.transform = `translateY(-${sidebarTransform}px)`
        }

        if (scrollTop <= maxSidebarScroll) {
          window.scrollTo(0, scrollTop)
          if (contentRef.current) {
            contentRef.current.style.transform = 'translateY(0)'
          }
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [headerHeight, sidebarHeight, isInitialized, viewportHeight])

  return (
    <div className="relative min-h-screen">
      <div
        ref={contentRef}
        className="lg:flex"
        style={{
          minHeight: `${Math.max(viewportHeight, sidebarHeight)}px`,
          willChange: 'transform',
        }}
      >
        <div
          className={`lg:w-[calc(100%-650px)] w-full lg:h-[calc(100vh-80px)] lg:sticky lg:top-[80px] lg:pt-[100px] overflow-hidden ${
            styles.leftBG
          } ${viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '' : 'g-left-container'} lg:pr-[55px]`}
        >
          <NFTAssetSection
            nftType={nftType}
            nftAssetUrl={nftAssetUrl}
            nftCoverUrl={nftCoverUrl}
            originalCoverUrl={originalCoverUrl}
            fullScreen={fullScreen}
          />

          <div className="w-full lg:flex hidden mt-[32px] justify-between">
            <div className=""></div>

            <div className="flex gap-[15px] items-center">
              {nftType.includes('vr') || nftType.includes('image') ? (
                <div className={styles.expandIconContainer}>
                  <div onClick={() => setFullScreenView(nftType.includes('vr') ? nftAssetUrl : nftCoverUrl)}>
                    <FullScreenModeIcon />
                  </div>
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>

        <div className="w-full h-auto lg:w-[650px] lg:h-[calc(100vh-80px)] overflow-hidden lg:sticky lg:top-[80px]">
          <div
            ref={['xs', 'sm', 'md'].includes(viewport) ? undefined : sidebarRef}
            style={['xs', 'sm', 'md'].includes(viewport) ? { transform: 'translateY(0)' } : { willChange: 'transform' }}
            className={`w-full z-10 pt-[15px] lg:pt-[0px] ${
              viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? 'g-nft-container' : 'g-nft-right-container'
            } lg:pl-[100px] lg:pb-[100px]`}
          >
            {nftLoading || editionLoading || nameLoading ? (
              <div className="w-full">
                <div className="w-[265] h-[40px] rounded-[5px] mt-[90px] lg:mt-[135px] overflow-hidden">
                  <div className="skeletonLoader" />
                </div>
                <div className="w-[265px] h-[40px] mt-[30px] rounded-[5px] overflow-hidden">
                  <div className="skeletonLoader" />
                </div>

                <div className="w-[190px] h-[40px] mt-[30px] rounded-[5px] overflow-hidden">
                  <div className="skeletonLoader" />
                </div>

                <div className="w-[120px] h-[40px] mt-[30px] rounded-[5px] overflow-hidden">
                  <div className="skeletonLoader" />
                </div>
              </div>
            ) : showListEditionDetail ? (
              <div className="">
                <ListEditionDetail
                  data={nftDetail}
                  listingType={listEditionType}
                  onSuccess={nft => {
                    handleSuccessListEdition(nft)
                    setShowListEditionDetail(false)
                  }}
                  fromNFT={true}
                  onClose={() => {
                    setShowListEditionDetail(false)
                  }}
                  merchItem={merchCard}
                />
              </div>
            ) : showListArtworkDetail ? (
              <div className="">
                <ListArtworkDetail
                  data={nftDetail}
                  onClose={(status: string, price) => {
                    if (status === 'listed') {
                      onListNftArtwork()
                    } else {
                      setShowListArtworkDetail(false)
                    }
                  }}
                />
              </div>
            ) : showAuctionDetail ? (
              <div>
                <AuctionDetail
                  data={nftDetail}
                  onSuccess={nft => handleSuccessAuction(nft)}
                  onRefreshPage={() => {
                    onStartAuction()
                    // router.reload()
                  }}
                  onClose={() => {
                    setShowAuctionDetail(false)
                  }}
                  fromNFT={true}
                />
              </div>
            ) : (
              <div className={styles.nftDetailCard}>
                <div className="w-full flex justify-end items-center">
                  <div className="flex items-center">
                    <div className="flex items-center gap-[6px]">
                      <Image
                        src={like ? HeartFilledRedIcon : HeartFilledWhiteIcon}
                        alt=""
                        className=""
                        onClick={() => changeLikeStatus()}
                      />
                      <div className={`${styles.likeCount}`} onClick={() => setShowLikeModal(true)}>
                        {likeCount}
                      </div>
                    </div>

                    <div className={`${styles.likeBorder} mx-[15px]`} />

                    <div className="flex items-center">
                      <Image
                        src={save ? TicketGreenIcon : TicketWhiteIcon}
                        alt=""
                        className=""
                        onClick={() => changeSaveStatus()}
                      />
                    </div>

                    <div className={`${styles.likeBorder} ml-[15px] mr-[7px]`} />

                    <div className="hidden items-center sm:flex">
                      <MenuEditNft
                        studio={true}
                        metadata={
                          nftType.includes('image') && nftType !== 'image/gif' && !nftCoverUrl.includes('ext=gif')
                        }
                        explorerURL={explorerURL}
                        setShowNftMeta={setShowNftMeta}
                        setRemoveMerchModal={setRemoveMerchModal}
                        setCreateMerchModal={setCreateMerchModal}
                        merchToggle={merchToggle}
                        isCreatedNft={
                          publicKey?.toBase58() === nftInfo?.creator_address &&
                          publicKey?.toBase58() === nftInfo?.owner_address
                        }
                        setOpenShareModal={setOpenShareModal}
                      />
                    </div>

                    <div
                      className="w-[32px] h-[32px] flex items-center justify-center sm:hidden"
                      onClick={() => setShowNftMenuEditModal(true)}
                    >
                      <ThreeDotIcon alt="" />
                    </div>
                  </div>
                </div>

                <div className={styles.rightContainer}>
                  <div className={styles.rightTopSection}>
                    <div className="w-full">
                      {(editionType === 'print_oe' || editionType === 'print_le') && (
                        <div className={styles.editionName}>{`Edition Print #${editionDetail?.print?.number}`}</div>
                      )}
                      <div className="w-full mt-[45px]">
                        <div className={styles.nftTitle}>{nftName}</div>
                      </div>

                      {tempNftDescription?.length > 0 && (
                        <div className="w-full mt-[30px]">
                          <NFTDescriptionSection description={tempNftDescription} />
                        </div>
                      )}

                      <div className="w-full lg:min-h-[200px] lg:h-[calc(100vh-400px)] lg:flex lg:flex-col lg:justify-between">
                        <NFTCreatorDataSection
                          nftCreatorname={nftCreatorname}
                          nftCollectionAddress={nftCollectionAddress}
                          nftCollectionName={nftCollectionName}
                          nftCollectionImage={nftCollectionImage}
                          profileImage={nftCreatorUrl}
                        />

                        <div>
                          {(supply?.max === null || supply?.max > 0) && (nftLoading || editionLoading) && merchCard ? (
                            <div className="w-full h-[45px]"></div>
                          ) : merchCard ? (
                            <div className="w-full mb-[15px]">
                              <NFTEditionMerchCard
                                url={merchCard.options.addons[0].mockup_url}
                                name={merchCard.name}
                                setFullScreenMerch={setFullScreenMerch}
                              />
                            </div>
                          ) : (
                            <div className="w-full h-[45px]"></div>
                          )}

                          <div className={`w-full ${styles.nftDetailCardPadding}`}>
                            <div>
                              {isNftMasterEdition() ? (
                                <div className="w-full flex flex-col sm:mx-auto sm:w-full sm:flex sm:flex-row sm:gap-4">
                                  <MintEditionCard
                                    editionType={editionType}
                                    maxSupply={editionDetail.maxSupply}
                                    currentSupply={currentSupply}
                                    buyLimit={nftInfo?.master_edition?.buyLimit}
                                    isEnded={isEnded}
                                    isStarted={isStarted}
                                    nftPrice={nftPrice}
                                    merchCost={
                                      nftInfo?.master_edition?.merchProduct?.selling_price
                                        ? nftInfo?.master_edition?.merchProduct?.selling_price / 100 / solToUsdRate
                                        : null
                                    }
                                    setPrice={setPrice}
                                    mintQty={mintQty}
                                    setMintQty={setMintQty}
                                    mintEditionLoading={mintEditionLoading}
                                    mintEdition={mintEdition}
                                    endEditionTime={nftInfo?.master_edition?.endTime}
                                    startEditionTime={nftInfo?.master_edition?.startTime}
                                    setIsEnded={setIsEnded}
                                    setIsStarted={setIsStarted}
                                    liveAuction={liveAuction}
                                    isEditionListing={isEditionListing}
                                    isOwnNft={isOwnNft}
                                    cancelListEdition={cancelListEdition}
                                    cancelListLoading={cancelListLoading}
                                    setListEditionType={setListEditionType}
                                    setShowListEditionDetail={setShowListEditionDetail}
                                  />
                                </div>
                              ) : isNftNormalListing() ? ( // NFT Listing
                                <NFTMakeOfferSection
                                  nftPrice={nftPrice}
                                  solToUsdRate={solToUsdRate}
                                  isOwnNft={isOwnNft}
                                  viewport={viewport}
                                  connected={connected}
                                  setVisible={setVisible}
                                  setOpenOfferModal={setOpenOfferModal}
                                  tradeStateAddress={tradeStateAddress}
                                  buyLoading={buyLoading}
                                  quickBuy={quickBuy}
                                  setShowUpdateListNFTModal={setShowUpdateListNFTModal}
                                />
                              ) : isNftAuctionLive() ? ( // NFT Live Auction
                                <div className={`w-full ${styles.auctionContainer}`}>
                                  <AuctionCard
                                    currentPrice={nftCurrentPrice}
                                    reservePrice={nftReservePrice}
                                    endTime={endTime}
                                    startTime={startTime}
                                    setIsEnded={setIsEnded}
                                    setIsStarted={setIsStarted}
                                    liveAuction={liveAuction}
                                    isEditionListing={isEditionListing}
                                  />
                                  {isNftAuctionEnded() ? (
                                    <div className="w-full mt-[15px]">
                                      {isFinalizeProcessing ? (
                                        <LoadingButton onClick={() => {}} fullWidth />
                                      ) : nftCurrentPrice > 0 ? (
                                        <GradientButton
                                          label="Finalize Sale"
                                          fullWidth
                                          onClick={() => processFinalizeSale()}
                                        />
                                      ) : isCancelProcessing ? (
                                        <LoadingButton onClick={() => {}} fullWidth />
                                      ) : (
                                        <GradientButton
                                          label="Cancel Auction"
                                          fullWidth
                                          onClick={() => cancelAuction()}
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    isOwnNft === true && (
                                      <div className="mt-[15px]">
                                        {isCancelProcessing ? (
                                          <LoadingButton onClick={() => {}} fullWidth />
                                        ) : (
                                          <GradientButton
                                            label="Cancel Auction"
                                            fullWidth
                                            onClick={() => nftCurrentPrice === 0 && cancelAuction()}
                                            disabled={nftCurrentPrice > 0}
                                          />
                                        )}
                                      </div>
                                    )
                                  )}

                                  {showPlaceBidButton && !isOwnNft && (
                                    <NFTPlaceBidSection
                                      publicKey={publicKey}
                                      highBidder={highBidder}
                                      connected={connected}
                                      setVisible={setVisible}
                                      viewport={viewport}
                                      price={price}
                                      setPrice={setPrice}
                                      isEnded={isEnded}
                                      mainWalletBalance={mainWalletBalance}
                                      minBidAmount={minBidAmount}
                                      biddingBalance={biddingBalance}
                                      bidWalletLock={bidWalletLock}
                                      endPhaseBidIncrement={nftInfo?.auction?.auction?.end_phase_bid_increment}
                                      showAlert={() => {
                                        setAlertData([
                                          {
                                            id: 0,
                                            title: 'Insufficient funds',
                                            description: 'Not enough funds in wallet for bid amount',
                                            type: 'error',
                                          },
                                        ])
                                        setShowAlert(true)
                                      }}
                                      placeBidAndSave={placeBidAndSave}
                                      auctionLoading={auctionLoading}
                                      endPhaseDuration={endPhaseDuration}
                                      auctionInfoFocus={auctionInfoFocus}
                                      setAuctionInfoFocus={setAuctionInfoFocus}
                                    />
                                  )}
                                </div>
                              ) : nftInfo ? ( // NFT Normal
                                isOwnNft === true ? (
                                  <div className="w-full flex flex-col gap-[10px]">
                                    <GradientButton
                                      fullWidth
                                      label="List Artwork"
                                      onClick={() => setShowListArtworkDetail(true)}
                                    />

                                    <OutlineButton
                                      fullWidth
                                      label="Start Auction"
                                      onClick={() => {
                                        setShowAuctionDetail(true)
                                      }}
                                    />
                                  </div>
                                ) : !isNftMasterEdition() ? ( // Print Edition
                                  <div className="w-full flex gap-[20px]">
                                    <OutlineButton
                                      fullWidth
                                      label="Make Offer"
                                      onClick={() => {
                                        if (!connected) {
                                          setVisible(true)
                                          return
                                        }
                                        setOpenOfferModal(true)
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <></>
                                )
                              ) : (
                                <></>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="w-full mt-[100px]">
                        <NFTHistorySection mintAddress={nftDetail.mintAddress} />
                      </div>
                    </div>
                  </div>

                  <div className="w-full mt-[15px]">
                    <NFTDetailSection
                      nftOwnerUsername={nftOwnerUsername}
                      nftType={nftType}
                      nftCoverUrl={nftCoverUrl}
                      editionDetail={editionDetail}
                      editionType={editionType}
                      supply={supply}
                      isNftMasterEdition={isNftMasterEdition()}
                      isNftPrintEdition={isNftPrintEdition()}
                      royalties={nftRoyalties}
                    />
                    {nftCategories.length > 0 && <NFTCategorySection nftCategories={nftCategories} />}
                  </div>

                  {merchToggle && (
                    <div className="w-full">
                      <ShopApparelSection
                        title="Shop Apparel"
                        type="apparel"
                        nftDetail={nftDetail}
                        nftCreator={nftCreatorname}
                        shopApparelList={ShopApparelList}
                        openMerchPage={data => {
                          setMerchToggle(false)
                          setMerchData(data)
                          setOpenMerchPage(true)
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showListEditionDetail || showListArtworkDetail || showAuctionDetail ? (
        <></>
      ) : (
        <div>
          <div className="w-full mt-[45px]">
            <NFTMerchSection username={nftCreatorname} userId={nftCreatorId} label="Shop Merch" />
          </div>

          <div className="w-full mt-[45px]">
            <NFTMoreSection nftAddress={mintAddress} wallet={nftInfo?.creator_address} username={nftCreatorname} />
          </div>

          <div className="w-full h-[200px]"></div>
        </div>
      )}

      {openProgressingModal && <ProgressingModal open={openProgressingModal} />}

      {showNftMenuEditModal && (
        <NftMenuEditModal
          open={showNftMenuEditModal}
          onClose={() => setShowNftMenuEditModal(false)}
          studio={true}
          metadata={nftType.includes('image') && nftType !== 'image/gif' && !nftCoverUrl.includes('ext=gif')}
          explorerURL={explorerURL}
          setShowNftMeta={setShowNftMeta}
          setRemoveMerchModal={setRemoveMerchModal}
          setCreateMerchModal={setCreateMerchModal}
          merchToggle={merchToggle}
          isCreatedNft={
            publicKey?.toBase58() === nftInfo?.creator_address && publicKey?.toBase58() === nftInfo?.owner_address
          }
          setOpenShareModal={setOpenShareModal}
        />
      )}
      <UpdateNFTModal
        data={nftListingData}
        open={showUpdateListNFTModal}
        onClose={() => setShowUpdateListNFTModal(false)}
        onCancelList={onCancelList}
        onUpdatePrice={onUpdatePrice}
      />

      {showLikeModal && publicKey && (
        <LikeModal
          open={showLikeModal}
          ownWallet={publicKey?.toBase58()}
          close={() => setShowLikeModal(false)}
          type="liked"
          wallet={mintAddress}
        />
      )}

      {showSaveModal && publicKey && (
        <LikeModal
          open={showSaveModal}
          ownWallet={publicKey?.toBase58()}
          close={() => setShowSaveModal(false)}
          type="saved"
          wallet={mintAddress}
        />
      )}

      {showNftMeta && nftInfo && (
        <EditMetaModal
          open={showNftMeta}
          close={status => {
            if (status === 'update') router.reload()
            setShowNftMeta(false)
          }}
          nftCover={nftCoverUrl}
          originalCoverUrl={originalCoverUrl}
          nftAsset={nftAssetUrl}
          nftType={nftType}
          nftInfo={nftInfo}
          creatorRoyalty={nftRoyalties}
          nftRoyalties={nftDetail.creators}
          nftAttributes={nftDetail?.attributes}
        />
      )}

      <ModalPlaceBid
        balance={mainWalletBalance}
        biddingWalletBalance={biddingBalance}
        biddingLockBalance={bidWalletLock}
        nftName={nftName}
        nftUrl={nftCoverUrl}
        currentPrice={nftCurrentPrice}
        reservePrice={nftReservePrice}
        placeBid={placeBidAndSave}
        open={showModalPlaceBid}
        loading={auctionLoading}
        onClose={() => {
          setShowModalPlaceBid(false)
        }}
      />

      <Alert
        open={showAlert}
        close={() => {
          setShowAlert(false)
        }}
        data={alertData}
      />
      {openReportModal && (
        <ReportAnIssueModal
          siteUrl={`/nft/${id}`}
          wallet={connected ? publicKey?.toString() : null}
          open={openReportModal}
          close={() => {
            setOpenReportModal(false)
          }}
        />
      )}
      {openOfferModal && (
        <MakeAnOfferModal
          open={openOfferModal}
          close={() => setOpenOfferModal(false)}
          onClick={price => makeOffer(price)}
          nftName={nftName}
          collectionName={nftCollectionName}
          listingPrice={nftPrice}
          offerBalance={offerBalance}
          mainWalletBalance={mainWalletBalance}
        />
      )}
      {deficencyModal && (
        <EscrowDeficientModal
          onFinalize={() => finalizeSale(finalizeData)}
          open={deficencyModal}
          close={() => setDeficencyModal(false)}
          highestBid={nftCurrentPrice}
          deficiency={deficiency}
          nftName={nftName}
          collectionName={nftCollectionName}
        />
      )}
      {createMerchModal && (
        <CreateMerchSudioModal
          onContinue={() => enableMerchToggle(true)}
          open={createMerchModal}
          close={() => setCreateMerchModal(false)}
        />
      )}
      {removeMerchModal && (
        <RemoveMerchSudioModal
          onContinue={() => enableMerchToggle(false)}
          open={removeMerchModal}
          close={() => setRemoveMerchModal(false)}
        />
      )}
      {openShareModal && (
        <SocialShareModal
          open={openShareModal}
          close={() => setOpenShareModal(false)}
          vote={share}
          setVote={setShare}
          voteCount={shareCount}
          setVoteCount={setShareCount}
          message={'Check out this fresh art on @foster_protocol #fostermarketplace'}
          url={`/nft/${id}`}
        />
      )}
      {openReportMenuModal && (
        <ReportMobileModal
          open={openReportMenuModal}
          close={() => setOpenReportMenuModal(false)}
          address={id}
          onClick={() => {
            setOpenReportMenuModal(false)
            setOpenReportModal(true)
          }}
          nftType={nftType}
        />
      )}
    </div>
  )
}

export default MainSection
